import { inject, InjectionToken } from '@angular/core';
import { Task, TaskCompletionGateOverrideRequest } from '@jms/model';

/** Resends the refused write with what the user authorised added to the same body; only an app that may offer it ever calls it. */
export type TaskCompletionResend = (authorised: TaskCompletionGateOverrideRequest) => Promise<Task | null>;

/** True when the refusal was shown and nothing was written; the record when an authorised override saved it; false to let the write fail as before. */
export type TaskCompletionRefusalOutcome = boolean | { saved: Task | null };

/** Shows a refused completion its own way and answers true, or answers false to let the command fail as before. */
export type TaskCompletionRefusalHandler = (error: unknown, resendWithOverride?: TaskCompletionResend) => TaskCompletionRefusalOutcome | Promise<TaskCompletionRefusalOutcome>;

export const TASK_COMPLETION_REFUSAL_HANDLER = new InjectionToken<TaskCompletionRefusalHandler>('work.task.TASK_COMPLETION_REFUSAL_HANDLER');

/** The app's handler, or none: an app that registers nothing keeps the command's own failure. */
export function injectTaskCompletionRefusalHandler(): TaskCompletionRefusalHandler {

  return inject(TASK_COMPLETION_REFUSAL_HANDLER, { optional: true }) ?? (() => false);
}

export interface TaskWriteResult {
  readonly refused: boolean;
  readonly saved?: Task | null;
}

// Every completing screen sends through here, so a refusal the app showed leaves the screen as it was and any other failure still fails it.
export async function sendTaskWrite(send: () => Promise<Task | null>, resendWithOverride: TaskCompletionResend | undefined, onRefused: TaskCompletionRefusalHandler): Promise<TaskWriteResult> {

  try {
    return { refused: false, saved: await send() };
  } catch (error: unknown) {
    const outcome = await onRefused(error, resendWithOverride);
    if (outcome === false) throw error;

    return outcome === true
      ? { refused: true }
      : { refused: false, saved: outcome.saved };
  }
}
